// File: frontend/src/pages/SeoAnalyticsPage.js
// Version: 1.3
// Date: 2026-04-28
// Description: SEO Analytics Dashboard — domain overview, position tracking,
//              trend charts (recharts), and competitor gap analysis. Phase 07-03
//              extended this with Ahrefs tabs (Overview, Backlinks, Organic
//              Traffic, AI Citations) and a traffic-decline alert banner.
//              Phase 07-04 added the Full Report tab with Ahrefs deep-links.
// History:
// - [v1.3]: Added Full Report tab to the Ahrefs tab group, mounting
//           AhrefsFullReportTab and forwarding the selected domain so the
//           Site Explorer link can target the correct domain.
// - [v1.2]: Light editorial restyle to match Voice page — light bg
//           (#f8fafc), inline-style page wrapper, underline-style tab nav,
//           pill-style range selector. No data flow changes.
// - [v1.1]: Added Ahrefs tab group (Overview/Backlinks/Organic Traffic/AI
//           Citations), AhrefsAlertBanner mounted above tab nav, divider
//           between SerpAPI and Ahrefs tab groups.
// - [v1.0]: Initial SerpAPI dashboard.

import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import toast from 'react-hot-toast';
import seoAnalyticsService from '../services/seoAnalyticsService';
import AhrefsAlertBanner from '../components/seo-analytics/AhrefsAlertBanner';
import AhrefsOverviewTab from '../components/seo-analytics/AhrefsOverviewTab';
import AhrefsBacklinksTab from '../components/seo-analytics/AhrefsBacklinksTab';
import AhrefsTrafficTab from '../components/seo-analytics/AhrefsTrafficTab';
import AhrefsCitationsTab from '../components/seo-analytics/AhrefsCitationsTab';
import AhrefsFullReportTab from '../components/seo-analytics/AhrefsFullReportTab';
import './SeoAnalyticsPage.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return dateStr;
  }
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return 'No data yet';
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
};

const positionBadgeClass = (position) => {
  if (!position || position === 0) return 'badge-gray';
  if (position <= 3) return 'badge-green';
  if (position <= 10) return 'badge-yellow';
  return 'badge-red';
};

const positionLabel = (position) => {
  if (!position || position === 0) return 'Not ranked';
  return `#${position}`;
};

const statusBadgeClass = (status) => {
  if (status === 'active') return 'badge-green';
  if (status === 'error') return 'badge-red';
  return 'badge-gray';
};

const gapStyle = (gap) => {
  if (gap < 0) return { color: '#16a34a', fontWeight: 700 };
  if (gap > 0) return { color: '#dc2626', fontWeight: 700 };
  return { color: '#6b7280' };
};

const gapArrow = (gap) => {
  if (gap < 0) return ' ↓';
  if (gap > 0) return ' ↑';
  return '';
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const DomainCard = ({ domain, onClick }) => (
  <div
    className="domain-card"
    onClick={() => onClick(domain)}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === 'Enter' && onClick(domain)}
  >
    <div className="domain-card-header">
      <span className="domain-url">{domain.url}</span>
      <span className={`badge ${statusBadgeClass(domain.status)}`}>
        {domain.status || 'unknown'}
      </span>
    </div>
    <div className="domain-card-stats">
      <div className="stat">
        <span className="stat-label">Keywords</span>
        <span className="stat-value">{domain.keyword_count ?? 0}</span>
      </div>
      <div className="stat">
        <span className="stat-label">Avg Position</span>
        <span className="stat-value">
          {domain.avg_position != null ? `#${domain.avg_position.toFixed(1)}` : '—'}
        </span>
      </div>
      <div className="stat">
        <span className="stat-label">Last Collected</span>
        <span className="stat-value">{formatFullDate(domain.latest_snapshot_date)}</span>
      </div>
    </div>
  </div>
);

const PositionsTab = ({ positions }) => {
  if (!positions.length) {
    return (
      <p className="empty-state">No position data available. Run a position collection first.</p>
    );
  }

  const sorted = [...positions].sort((a, b) => {
    if (!a.position) return 1;
    if (!b.position) return -1;
    return a.position - b.position;
  });

  const ranked = sorted.filter((p) => p.position && p.position > 0);
  const avgPos =
    ranked.length > 0
      ? (ranked.reduce((s, p) => s + p.position, 0) / ranked.length).toFixed(1)
      : null;

  return (
    <div>
      <div className="positions-summary">
        <span>{sorted.length} keywords tracked</span>
        {avgPos && <span>Avg position: <strong>#{avgPos}</strong></span>}
      </div>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Keyword</th>
              <th>Position</th>
              <th>URL</th>
              <th>Search Volume</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p, i) => (
              <tr key={i}>
                <td className="keyword-cell">{p.keyword}</td>
                <td>
                  <span className={`badge ${positionBadgeClass(p.position)}`}>
                    {positionLabel(p.position)}
                  </span>
                </td>
                <td>
                  {p.url ? (
                    <a href={p.url} target="_blank" rel="noreferrer" className="url-link">
                      {p.url.replace(/^https?:\/\//, '').substring(0, 40)}
                      {p.url.length > 40 ? '…' : ''}
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{p.search_volume ?? '—'}</td>
                <td>{formatFullDate(p.collected_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TrendsTab = ({ trends, dateRange, onDateRangeChange, loading }) => {
  if (loading) {
    return <p className="empty-state">Loading trend data…</p>;
  }

  if (!trends.length) {
    return (
      <div>
        <DateRangeSelector dateRange={dateRange} onChange={onDateRangeChange} />
        <p className="empty-state">
          No trend data available. Positions will be collected daily.
        </p>
      </div>
    );
  }

  // Top 5 keywords by most recent position (lowest number = best)
  const topKeywords = [...trends]
    .map((t) => {
      const points = [...(t.data_points || [])].sort(
        (a, b) => new Date(b.collected_date) - new Date(a.collected_date)
      );
      return { keyword: t.keyword, points, latestPos: points[0]?.position ?? 999 };
    })
    .sort((a, b) => a.latestPos - b.latestPos)
    .slice(0, 5);

  return (
    <div>
      <DateRangeSelector dateRange={dateRange} onChange={onDateRangeChange} />
      <div className="charts-grid">
        {topKeywords.map(({ keyword, points }) => {
          const chartData = points
            .slice()
            .reverse()
            .map((dp) => ({
              date: formatDate(dp.collected_date),
              position: dp.position,
            }));

          const positions = chartData.map((d) => d.position).filter(Boolean);
          const maxPos = positions.length ? Math.max(...positions) + 2 : 20;

          return (
            <div key={keyword} className="chart-card">
              <h4 className="chart-title">{keyword}</h4>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis
                      domain={[1, maxPos]}
                      reversed
                      tick={{ fontSize: 11 }}
                      label={{ value: 'Rank', angle: -90, position: 'insideLeft', fontSize: 11 }}
                    />
                    <Tooltip
                      formatter={(val) => [`#${val}`, 'Position']}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="position"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DateRangeSelector = ({ dateRange, onChange }) => (
  <div
    className="date-range-selector"
    style={{
      display: 'inline-flex',
      padding: '4px',
      backgroundColor: '#f1f5f9',
      borderRadius: '0.5rem',
      gap: '2px',
      marginBottom: '1rem',
    }}
  >
    {[7, 30, 90].map((days) => {
      const isActive = dateRange === days;
      return (
        <button
          key={days}
          onClick={() => onChange(days)}
          className={`range-btn ${isActive ? 'range-btn-active' : 'range-btn-inactive'}`}
          style={{
            padding: '0.375rem 0.75rem',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            backgroundColor: isActive ? '#ffffff' : 'transparent',
            color: isActive ? '#0f172a' : '#64748b',
            boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            transition: 'background 0.15s, color 0.15s',
          }}
        >
          {days}d
        </button>
      );
    })}
  </div>
);

const CompetitorsTab = ({ competitors, loading }) => {
  if (loading) {
    return <p className="empty-state">Loading competitor data…</p>;
  }

  if (!competitors.length) {
    return (
      <p className="empty-state">
        No competitors configured. Add competitors via the admin panel.
      </p>
    );
  }

  return (
    <div className="competitors-list">
      {competitors.map((comp, ci) => (
        <div key={ci} className="competitor-block">
          <div className="competitor-header">
            <h4 className="competitor-url">{comp.competitor_url}</h4>
            <span style={gapStyle(comp.avg_gap)}>
              Avg Gap: {comp.avg_gap > 0 ? '+' : ''}{comp.avg_gap?.toFixed(1) ?? '—'}
              {gapArrow(comp.avg_gap)}
            </span>
          </div>
          {comp.positions && comp.positions.length > 0 ? (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Our Position</th>
                    <th>Their Position</th>
                    <th>Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {comp.positions.map((row, ri) => (
                    <tr key={ri}>
                      <td className="keyword-cell">{row.keyword}</td>
                      <td>
                        <span className={`badge ${positionBadgeClass(row.our_position)}`}>
                          {positionLabel(row.our_position)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${positionBadgeClass(row.competitor_position)}`}>
                          {positionLabel(row.competitor_position)}
                        </span>
                      </td>
                      <td style={gapStyle(row.gap)}>
                        {row.gap > 0 ? '+' : ''}{row.gap ?? '—'}{gapArrow(row.gap)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No keyword data for this competitor.</p>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const SeoAnalyticsPage = () => {
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [activeTab, setActiveTab] = useState('positions');
  const [positions, setPositions] = useState([]);
  const [trends, setTrends] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [dateRange, setDateRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  // Track which tabs have been loaded to avoid redundant fetches
  const [loadedTabs, setLoadedTabs] = useState({});

  // Fetch domain list on mount
  useEffect(() => {
    const fetchDomains = async () => {
      setLoading(true);
      try {
        const data = await seoAnalyticsService.getDomains();
        setDomains(data || []);
      } catch (err) {
        console.error('[SeoAnalyticsPage] Failed to fetch domains:', err);
        toast.error('Failed to load domains. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchDomains();
  }, []);

  // Fetch positions when a domain is selected
  useEffect(() => {
    if (!selectedDomain) return;
    const fetchPositions = async () => {
      setTabLoading(true);
      try {
        const data = await seoAnalyticsService.getPositions(selectedDomain.id);
        setPositions(data || []);
        setLoadedTabs({ positions: true });
      } catch (err) {
        console.error('[SeoAnalyticsPage] Failed to fetch positions:', err);
        toast.error('Failed to load positions.');
      } finally {
        setTabLoading(false);
      }
    };
    fetchPositions();
    // Reset other tabs state for new domain
    setTrends([]);
    setCompetitors([]);
    setActiveTab('positions');
    setLoadedTabs({});
  }, [selectedDomain]);

  // Fetch trends lazily (when tab is clicked, or date range changes)
  const fetchTrends = useCallback(
    async (days) => {
      if (!selectedDomain) return;
      setTabLoading(true);
      try {
        const data = await seoAnalyticsService.getTrends(selectedDomain.id, days);
        setTrends(data || []);
        setLoadedTabs((prev) => ({ ...prev, trends: true }));
      } catch (err) {
        console.error('[SeoAnalyticsPage] Failed to fetch trends:', err);
        toast.error('Failed to load trend data.');
      } finally {
        setTabLoading(false);
      }
    },
    [selectedDomain]
  );

  // Fetch competitors lazily
  const fetchCompetitors = useCallback(async () => {
    if (!selectedDomain) return;
    setTabLoading(true);
    try {
      const data = await seoAnalyticsService.getCompetitors(selectedDomain.id);
      setCompetitors(data || []);
      setLoadedTabs((prev) => ({ ...prev, competitors: true }));
    } catch (err) {
      console.error('[SeoAnalyticsPage] Failed to fetch competitors:', err);
      toast.error('Failed to load competitor data.');
    } finally {
      setTabLoading(false);
    }
  }, [selectedDomain]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'trends' && !loadedTabs.trends) {
      fetchTrends(dateRange);
    }
    if (tab === 'competitors' && !loadedTabs.competitors) {
      fetchCompetitors();
    }
  };

  const handleDateRangeChange = (days) => {
    setDateRange(days);
    fetchTrends(days);
  };

  const handleSelectDomain = (domain) => {
    setSelectedDomain(domain);
  };

  const handleBack = () => {
    setSelectedDomain(null);
    setPositions([]);
    setTrends([]);
    setCompetitors([]);
    setLoadedTabs({});
  };

  // ---------------------------------------------------------------------------
  // Render: Loading
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div
        style={{
          padding: '1.5rem',
          backgroundColor: '#f8fafc',
          minHeight: '100vh',
          color: '#0f172a',
        }}
      >
        <div className="loading-state">Loading SEO Analytics…</div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Domain Detail
  // ---------------------------------------------------------------------------
  if (selectedDomain) {
    // Tab groups: SerpAPI tabs first, divider, then Ahrefs tabs.
    const SERP_TABS = [
      { key: 'positions', label: 'Positions' },
      { key: 'trends', label: 'Trends' },
      { key: 'competitors', label: 'Competitors' },
    ];
    const AHREFS_TABS = [
      { key: 'ahrefs-overview', label: 'Overview' },
      { key: 'ahrefs-backlinks', label: 'Backlinks' },
      { key: 'ahrefs-traffic', label: 'Organic Traffic' },
      { key: 'ahrefs-citations', label: 'AI Citations' },
      { key: 'ahrefs-full-report', label: 'Full Report' },
    ];

    const renderTabBtn = (tab) => {
      const isActive = activeTab === tab.key;
      return (
        <button
          key={tab.key}
          onClick={() => handleTabChange(tab.key)}
          className="tab-btn"
          aria-current={isActive ? 'page' : undefined}
          aria-label={`Switch to ${tab.label} tab`}
          style={{
            padding: '0.625rem 1rem',
            border: 'none',
            background: 'transparent',
            color: isActive ? '#0f172a' : '#64748b',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: '-1px',
            transition: 'color 0.15s, border-color 0.15s, background 0.15s',
            borderRadius: 0,
          }}
          onMouseEnter={(e) => {
            if (!isActive) e.currentTarget.style.background = 'rgba(0,0,0,0.04)';
          }}
          onMouseLeave={(e) => {
            if (!isActive) e.currentTarget.style.background = 'transparent';
          }}
        >
          {tab.label}
        </button>
      );
    };

    return (
      <div
        style={{
          padding: '1.5rem',
          backgroundColor: '#f8fafc',
          minHeight: '100vh',
          color: '#0f172a',
        }}
      >
        <AhrefsAlertBanner domainId={selectedDomain.id} />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.25rem',
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={handleBack}
            style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              padding: '0.375rem 0.875rem',
              borderRadius: '0.5rem',
              color: '#2563eb',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
          >
            &lsaquo; All Domains
          </button>
          <h2
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#0f172a',
              margin: 0,
              flex: 1,
              wordBreak: 'break-all',
            }}
          >
            {selectedDomain.url}
          </h2>
          <span className={`badge ${statusBadgeClass(selectedDomain.status)}`}>
            {selectedDomain.status}
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            marginBottom: '1.5rem',
            borderBottom: '1px solid #e5e7eb',
            flexWrap: 'wrap',
            paddingLeft: '0.25rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} role="tablist" aria-label="SerpAPI tabs">
            <span
              style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#94a3b8',
                paddingRight: '0.5rem',
              }}
            >
              SerpAPI
            </span>
            {SERP_TABS.map(renderTabBtn)}
          </div>
          <div
            aria-hidden="true"
            style={{ width: '1px', height: '20px', background: '#e5e7eb', margin: '0 0.75rem' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} role="tablist" aria-label="Ahrefs tabs">
            <span
              style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#94a3b8',
                paddingRight: '0.5rem',
              }}
            >
              Ahrefs
            </span>
            {AHREFS_TABS.map(renderTabBtn)}
          </div>
        </div>

        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
            minHeight: '200px',
          }}
        >
          {activeTab === 'positions' && (
            tabLoading ? (
              <p className="empty-state">Loading positions…</p>
            ) : (
              <PositionsTab positions={positions} />
            )
          )}
          {activeTab === 'trends' && (
            <TrendsTab
              trends={trends}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              loading={tabLoading}
            />
          )}
          {activeTab === 'competitors' && (
            <CompetitorsTab competitors={competitors} loading={tabLoading} />
          )}
          {activeTab === 'ahrefs-overview' && (
            <AhrefsOverviewTab domainId={selectedDomain.id} />
          )}
          {activeTab === 'ahrefs-backlinks' && (
            <AhrefsBacklinksTab domainId={selectedDomain.id} />
          )}
          {activeTab === 'ahrefs-traffic' && (
            <AhrefsTrafficTab domainId={selectedDomain.id} />
          )}
          {activeTab === 'ahrefs-citations' && (
            <AhrefsCitationsTab domainId={selectedDomain.id} />
          )}
          {activeTab === 'ahrefs-full-report' && (
            <AhrefsFullReportTab domain={selectedDomain} />
          )}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Domain Overview Grid
  // ---------------------------------------------------------------------------
  return (
    <div
      style={{
        padding: '1.5rem',
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        color: '#0f172a',
      }}
    >
      <div className="page-header">
        <div>
          <h1 className="page-title">SEO Analytics Dashboard</h1>
          <p className="page-subtitle">
            Monitor keyword positions across {domains.length} connected site{domains.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {domains.length === 0 ? (
        <p className="empty-state">
          No domains found. Connect domains via the Organic SEO section first.
        </p>
      ) : (
        <div className="domains-grid">
          {domains.map((domain) => (
            <DomainCard key={domain.id} domain={domain} onClick={handleSelectDomain} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SeoAnalyticsPage;
